function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { motion, AnimatePresence } from 'framer-motion';
import cn from 'classnames';
import differenceBy from 'lodash/differenceBy';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import map from 'lodash/map';
import isObjectLike from 'lodash/isObjectLike';
import startsWith from 'lodash/startsWith';
import toLower from 'lodash/toLower';
import isEmpty from 'lodash/isEmpty';
import ChevronRight from 'svg/chevron-right.svg';
import XIcon from 'svg/x.svg';
import intlShape from 'shapes/intlShape';
import CheckboxRadio from 'components/Form/CheckboxRadio';
import Link from 'components/Link';
import messages from './messages';
var _React_PureComponent;
/**
 * Select component
 * @param {string} id - unique input id
 * @param {string} optionsFrom - list of objects with options for select
 * @param {string} valueKey - key in object from optionsFrom for option value
 * @param {string} labelKey - key in object from optionsFrom for option label
 * @param {string} [toKey] - key in object from optionsFrom for option link
 * @param {string} [value=""] - selected value
 * @param {Object} [labelMessage=null] - Intl message label for input label
 * @param {Object} [noValueMessage] - custom intl message visible if there's no selected value
 * @param {(string|Object)} [classes] - additional input classes
 * @param {(string|Object)} [selectedFieldClasses] - additional classes for a selected value field
 * @param {boolean} [isShort=false] - switch to short variant
 * @param {function} onChange - action that is triggered by select value change
 */ class Select extends (_React_PureComponent = React.PureComponent) {
    componentDidUpdate(prevProps, prevState) {
        if (prevProps.optionsFrom !== this.props.optionsFrom || prevProps.featuredFrom !== this.props.featuredFrom) {
            this.onSetOptions();
            if (this.props.value) {
                this.forceUpdate();
            }
        }
        if (prevProps.value !== this.props.value && this.props.value !== this.state.value) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                value: this.props.value
            });
        }
        if (prevState.isDropdownOpen !== this.state.isDropdownOpen && this.state.isDropdownOpen && (this.state.value || this.state.value === 0)) {
            const currentOptionIdx = findIndex(this.fullOptionsFrom, {
                [this.props.valueKey]: this.state.value
            });
            if (currentOptionIdx >= 0) {
                this.currentFullIdx = currentOptionIdx;
                this.onScrollToOption(get(this.fullOptionsFrom, [
                    currentOptionIdx,
                    this.props.labelKey
                ]));
            }
        }
    }
    onSetOptions() {
        if (this.props.featuredFrom) {
            this.featuredLength = this.props.featuredFrom.length;
        }
        this.fullOptionsFrom = [
            ...this.props.featuredFrom || [],
            ...this.props.optionsFrom
        ];
        this.fullLength = this.fullOptionsFrom.length;
    }
    onSelectChange(value) {
        if (this.props.isDisabled) {
            return;
        }
        this.setState(()=>({
                value,
                isDropdownOpen: false
            }));
        this.props.onChange({
            id: this.props.id,
            value
        });
    }
    onSelectToggle() {
        if (this.props.isDisabled) {
            return;
        }
        this.setState((prevState)=>({
                isDropdownOpen: !prevState.isDropdownOpen
            }));
    }
    onInputFocus() {
        if (this.props.isDisabled) {
            return;
        }
        this.setState({
            isDropdownOpen: true
        });
    }
    onInputBlur() {
        this.setState({
            isDropdownOpen: false,
            value: this.props.value
        });
    }
    onScrollToOption(label) {
        const featured = this.currentFullIdx + 1 <= this.featuredLength;
        const option = get(this.options, `${label}-${featured ? 'feat' : 'main'}`, get(this.options, `${label}-main`));
        if (!isEmpty(option)) {
            this.list.scrollTop = option.offsetTop - 2 * option.offsetHeight;
        }
    }
    onFocusPreviousOption() {
        if (this.currentFullIdx - 1 >= 0) {
            this.currentFullIdx--;
            const label = get(this.fullOptionsFrom, [
                this.currentFullIdx,
                this.props.labelKey
            ]);
            this.onScrollToOption(label);
            this.setState({
                value: get(this.fullOptionsFrom, [
                    this.currentFullIdx,
                    this.props.valueKey
                ])
            });
        }
    }
    onFocusNextOption() {
        if (this.currentFullIdx + 1 <= this.fullLength - 1) {
            this.currentFullIdx++;
            const label = get(this.fullOptionsFrom, [
                this.currentFullIdx,
                this.props.labelKey
            ]);
            this.onScrollToOption(label);
            this.setState({
                value: get(this.fullOptionsFrom, [
                    this.currentFullIdx,
                    this.props.valueKey
                ])
            });
        }
    }
    onKeyDown(event) {
        switch(event.key){
            case 'Tab':
                return;
            case 'Shift':
            case 'Control':
            case 'Alt':
            case 'AltGraph':
            case 'Backspace':
            case 'Meta':
                break;
            case 'Enter':
                event.preventDefault();
                this.onSelectChange(this.state.value);
                break;
            case 'Escape':
                this.onInputBlur();
                break;
            case 'ArrowUp':
                this.onFocusPreviousOption();
                break;
            case 'ArrowDown':
                this.onFocusNextOption();
                break;
            default:
                {
                    this.currentFullIdx = this.firstEntry(event.key);
                    const firstEntry = get(this.fullOptionsFrom, this.currentFullIdx);
                    if (firstEntry) {
                        this.onScrollToOption(get(firstEntry, this.props.labelKey));
                        this.setState({
                            value: get(firstEntry, this.props.valueKey)
                        });
                    }
                }
        }
        event.preventDefault();
    }
    onBadgeClick(e, result) {
        const { optionsFrom, valueKey } = this.props;
        e.preventDefault();
        const value = optionsFrom.find((option)=>option[valueKey] === result);
        if (!value) {
            return;
        }
        this.onSelectChange(value[valueKey]);
        this.onInputBlur();
    }
    get currentLabel() {
        const { multi, imgKey, onlyCurrentImg } = this.props;
        const defaultValue = this.props.intl.formatMessage(messages.select.default);
        if (!this.state.value && this.state.value !== 0 || isEmpty(this.state.value) && multi) {
            if (this.props.onlyCurrentImg) {
                return this.renderImg();
            }
            if (this.props.noValueMessage) {
                return this.props.intl.formatMessage(this.props.noValueMessage);
            }
            return defaultValue;
        }
        if (this.state.value === '') {
            return defaultValue;
        }
        if (multi) {
            return null;
        }
        // eslint-disable-next-line eqeqeq
        const option = find(this.fullOptionsFrom, (o)=>o[this.props.valueKey] == this.state.value);
        return /*#__PURE__*/ _jsxs("div", {
            className: "text--nowrap",
            children: [
                this.renderImg(option),
                imgKey && onlyCurrentImg ? null : get(option, this.props.labelKey, defaultValue)
            ]
        });
    }
    firstEntry(key) {
        clearTimeout(this.clearQuery);
        const keyValue = toLower(key);
        this.queryString += keyValue;
        this.clearQuery = setTimeout(()=>{
            this.queryString = '';
        }, 1000);
        return findIndex(this.fullOptionsFrom, (el)=>startsWith(toLower(el[this.props.labelKey]), this.queryString));
    }
    renderInput() {
        const { multi } = this.props;
        return /*#__PURE__*/ _jsx("input", {
            className: "select__input",
            id: this.props.id,
            onFocus: ()=>this.onInputFocus(),
            onBlur: ()=>this.onInputBlur(),
            onMouseDown: ()=>this.onSelectToggle(),
            onKeyDown: this.onKeyDown,
            defaultValue: this.currentLabel,
            autoComplete: "off",
            multiple: multi,
            readOnly: true
        });
    }
    renderSelectOption(option, keySuffix = 'main') {
        const value = get(option, this.props.valueKey);
        return /*#__PURE__*/ _jsx("option", {
            value: value,
            className: cn({
                'd-none': this.props.hiddenValues.includes(value)
            }),
            children: get(option, this.props.labelKey, '---')
        }, `${this.props.id}-${value}-${keySuffix}`);
    }
    /* eslint-disable jsx-a11y/control-has-associated-label */ renderSelect() {
        let options = [];
        if (this.props.featuredFrom) {
            options = differenceBy(this.props.optionsFrom, this.props.featuredFrom, this.props.valueKey);
        } else {
            options = this.props.optionsFrom;
        }
        return /*#__PURE__*/ _jsxs("select", {
            className: "select__native-select",
            value: this.state.value,
            onBlur: ()=>this.onInputBlur(),
            onChange: (evt)=>this.onSelectChange(evt.target.value),
            multiple: this.props.multi,
            children: [
                /*#__PURE__*/ _jsx("option", {
                    className: "d-none"
                }),
                this.props.featuredFrom ? map(this.props.featuredFrom, (option)=>this.renderSelectOption(option, 'feat')) : null,
                this.props.featuredFrom ? /*#__PURE__*/ _jsx("option", {
                    disabled: true,
                    value: "-----"
                }) : null,
                map(options, (option)=>this.renderSelectOption(option))
            ]
        });
    }
    renderMultiSelected() {
        const { multi, value, optionsFrom, valueKey } = this.props;
        if (!multi) {
            return null;
        }
        function getLabel(result) {
            const item = optionsFrom.find((option)=>option[valueKey] === result);
            if (!item) {
                return null;
            }
            return item.label;
        }
        let multiSelectWidth;
        if (this.multiSelectRef && this.multiSelectRef.current) {
            multiSelectWidth = this.multiSelectRef.current.clientWidth;
        }
        let fullNameWidthElements = 0;
        function estimateBadgeSize(txt, font) {
            const element = document.createElement('canvas');
            const context = element.getContext('2d');
            context.font = font;
            const tSize = {
                width: context.measureText(txt).width + 48,
                height: parseInt(context.font, 10)
            };
            return tSize;
        }
        let totalWidth = 0;
        value.forEach((result)=>{
            const { width } = estimateBadgeSize(getLabel(result), '14px Inter');
            totalWidth += width;
            if (totalWidth < multiSelectWidth - 40) {
                fullNameWidthElements += 1;
            }
        });
        return /*#__PURE__*/ _jsx("div", {
            ref: this.multiSelectRef,
            className: "select__multiWrapper",
            children: value.map((result, index)=>{
                if (index > fullNameWidthElements) {
                    return null;
                }
                if (index < fullNameWidthElements) {
                    return /*#__PURE__*/ _jsx("div", {
                        className: "select__multi__itemWrapper",
                        children: /*#__PURE__*/ _jsxs(Link, {
                            to: "",
                            onClick: (e)=>this.onBadgeClick(e, result),
                            className: "select__multi__item",
                            children: [
                                getLabel(result),
                                /*#__PURE__*/ _jsx(XIcon, {})
                            ]
                        }, `result-id-${result}`)
                    }, `selected-field-${result}`);
                }
                return /*#__PURE__*/ _jsx("div", {
                    className: "select__multi__itemWrapper",
                    children: /*#__PURE__*/ _jsxs(Link, {
                        to: "",
                        onClick: (e)=>e.preventDefault(),
                        className: "select__multi__item",
                        children: [
                            "+",
                            value.length - fullNameWidthElements
                        ]
                    }, `result-id-${result}`)
                }, `rest-selected-field-${value.length - fullNameWidthElements}`);
            })
        });
    }
    renderOptionMulti(option, keySuffix = 'main') {
        const value = get(option, this.props.valueKey);
        return /*#__PURE__*/ _jsxs("li", {
            className: cn('select__list__item d-flex align-items-center', {
                'select__list__item--active': this.state.value === value,
                'd-none': this.props.hiddenValues.includes(value)
            }),
            ref: (el)=>{
                this.options[`${get(option, this.props.labelKey)}-${keySuffix}`] = el;
            },
            onMouseDown: ()=>this.onSelectChange(value),
            children: [
                /*#__PURE__*/ _jsx(CheckboxRadio, {
                    inputValue: "true",
                    value: this.state.value.includes(value) ? 'true' : 'false',
                    onChange: ()=>{}
                }),
                this.props.toKey ? /*#__PURE__*/ _jsx(Link, {
                    to: get(option, this.props.toKey),
                    children: get(option, this.props.labelKey, '---')
                }) : get(option, this.props.labelKey)
            ]
        }, `${this.props.id}-${value}-${keySuffix}`);
    }
    renderImg(option) {
        if (this.props.imgKey) {
            return /*#__PURE__*/ _jsx("figure", {
                className: "select__list__item__img",
                children: get(option, this.props.imgKey, null)
            });
        }
        return null;
    }
    renderOption(option, keySuffix = 'main') {
        const { multi } = this.props;
        if (multi) {
            return this.renderOptionMulti(option, keySuffix);
        }
        const value = get(option, this.props.valueKey);
        return /*#__PURE__*/ _jsxs("li", {
            className: cn('select__list__item', {
                'select__list__item--active': this.state.value === value,
                'd-none': this.props.hiddenValues.includes(value)
            }),
            ref: (el)=>{
                this.options[`${get(option, this.props.labelKey)}-${keySuffix}`] = el;
            },
            onMouseDown: ()=>this.onSelectChange(value),
            children: [
                this.renderImg(option),
                /*#__PURE__*/ _jsx("span", {
                    children: this.props.toKey ? /*#__PURE__*/ _jsx(Link, {
                        to: get(option, this.props.toKey),
                        children: get(option, this.props.labelKey, '---')
                    }) : get(option, this.props.labelKey)
                })
            ]
        }, `${this.props.id}-${value}-${keySuffix}`);
    }
    renderSelectList() {
        if (!this.state.isDropdownOpen) {
            return null;
        }
        return /*#__PURE__*/ _jsx(motion.div, {
            initial: {
                height: 0
            },
            animate: {
                height: 'auto'
            },
            exit: {
                height: 0
            },
            transition: {
                ease: 'easeOut',
                duration: 0.15
            },
            className: cn('select__list-wrapper', this.props.wrapperClasses),
            children: /*#__PURE__*/ _jsxs("ul", {
                className: cn('select__list', {
                    'select__list--short': this.props.isShort,
                    'select__list--opened': this.state.isDropdownOpen
                }),
                ref: (list)=>{
                    this.list = list;
                },
                children: [
                    this.props.featuredFrom ? map(this.props.featuredFrom, (option)=>this.renderOption(option, 'feat')) : null,
                    this.props.featuredFrom ? /*#__PURE__*/ _jsx("li", {
                        className: "select__list__divider"
                    }) : null,
                    map(this.props.optionsFrom, (option)=>this.renderOption(option))
                ]
            })
        });
    }
    renderLabel(hasError) {
        if (!this.props.labelMessage) {
            return null;
        }
        return /*#__PURE__*/ _jsx("div", {
            className: this.props.isOneLiner ? 'col-4 col-lg-3 align-self-start' : 'col-12 mb-3',
            children: /*#__PURE__*/ _jsx("label", {
                className: cn('select-label d-flex align-items-start', {
                    'text--error': hasError
                }),
                htmlFor: this.props.id,
                children: /*#__PURE__*/ _jsx("span", {
                    className: "select-label-text",
                    children: isObjectLike(this.props.labelMessage) ? this.props.intl.formatMessage(this.props.labelMessage, _object_spread({}, this.props.labelMessage.values)) : this.props.labelMessage
                })
            })
        });
    }
    render() {
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                this.renderLabel(),
                /*#__PURE__*/ _jsxs("div", {
                    className: cn('select__selected-field-wrapper', this.props.className, {
                        'select__selected-field-wrapper--short': this.props.isShort,
                        'select__selected-field-wrapper--opened': this.state.isDropdownOpen
                    }),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: cn('select__selected-field', this.props.selectedFieldClasses, {
                                'select__selected-field--no-value': !this.props.value && this.props.value !== 0,
                                disabled: this.props.isDisabled,
                                'select__selected-field--only-img': this.props.onlyCurrentImg,
                                'select__selected-field--opened': this.state.isDropdownOpen,
                                'border-danger': this.props.hasError
                            }),
                            children: [
                                this.currentLabel,
                                this.renderMultiSelected(),
                                this.renderInput(),
                                this.renderSelect()
                            ]
                        }),
                        /*#__PURE__*/ _jsx(ChevronRight, {
                            className: "select__arrow"
                        }),
                        /*#__PURE__*/ _jsx(AnimatePresence, {
                            children: this.renderSelectList()
                        })
                    ]
                })
            ]
        });
    }
    constructor(props){
        super(props);
        [
            'onSelectChange',
            'onSelectToggle',
            'onInputFocus',
            'onInputBlur',
            'onKeyDown'
        ].forEach((func)=>{
            this[func] = this[func].bind(this);
        });
        this.state = {
            isDropdownOpen: false,
            value: props.value
        };
        this.multiSelectRef = /*#__PURE__*/ createRef();
        // this.selectedElements = [];
        this.fullOptionsFrom = [];
        this.currentFullIdx = 0;
        this.featuredLength = 0;
        this.fullLength = 0;
        // this.input = null;
        this.list = null;
        this.options = {};
        this.queryString = '';
        this.clearQuery = null;
        this.onSetOptions();
    }
}
_define_property(Select, "propTypes", {
    id: PropTypes.string,
    optionsFrom: PropTypes.array.isRequired,
    featuredFrom: PropTypes.array,
    valueKey: PropTypes.string.isRequired,
    labelKey: PropTypes.string.isRequired,
    imgKey: PropTypes.string,
    labelMessage: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ]),
    isOneLiner: PropTypes.bool,
    toKey: PropTypes.string,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.arrayOf(PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ]))
    ]),
    noValueMessage: PropTypes.object,
    className: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ]),
    selectedFieldClasses: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ]),
    wrapperClasses: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ]),
    isDisabled: PropTypes.bool,
    isShort: PropTypes.bool,
    onlyCurrentImg: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    hiddenValues: PropTypes.array,
    hasError: PropTypes.bool,
    multi: PropTypes.bool
});
_define_property(Select, "defaultProps", {
    id: '',
    value: '',
    isDisabled: false,
    isShort: false,
    hiddenValues: [],
    multi: false
});
export default injectIntl(Select);
